import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";

import { Ripple } from "primereact/ripple";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Button } from "primereact/button";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";

import NoDataCreated from "../../../../../Components/NoDataCreated";
import ErrorPage from "../../../../../Components/ErrorPage";
import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import GoBackButton from "../../../../../Components/GoBackButton";
import TextInput from "../../../../../Components/TextInput";
import { checkText } from "../../../../../Utils/Validator";
import { checkIsBuildingInspector } from "../../../../../Utils/LoginUserRoleName";
import { goToTop } from "../../../../../Components/goToTop";

import {
    SiteVisitReport as SiteVisitReportType,
    SiteVisitReportBuildingInspectorResponse,
    ResponseFileList,
    noteStatuses,
    SiteVisitReportNoteList,
} from "../../../../../Controllers/Types/SiteVisitReport/SiteVisitReportType";

import { fetchSiteVisitReports } from "../../../../../Controllers/Thunks/SiteVisitReportThunks";
import { sendSiteVisitReportViaEmail, signSiteVisitReportBi } from "../../../../../Api/SiteVisitReport/SiteVisitReportApiServices";
import { confirmDialog } from "primereact/confirmdialog";
import ImagePreview from "../../../../../Components/ImagePreview";

interface EmailInput {
    value: string;
    error: string;
}

const SiteVisitReportPreview: React.FC = () => {
    const toast = useRef<Toast>(null);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const projectData = location.state?.projectData;
    const folderId = Number(projectData.folder_id);
    const isShared: boolean = projectData?.sharedBy !== null;
    const canEdit: boolean = Boolean(projectData?.can_edit);

    const [loading, setLoading] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    const {
        siteVisitReportLoading, siteVisitReportError, siteVisitReports
    } = useAppSelector((state) => state?.siteVisitReport);

    const [siteVisitReportData, setSiteVisitReportData] = useState<SiteVisitReportType | null>(null);
    const [siteVisitReportNotesData, setSiteVisitReportNotesData] = useState<SiteVisitReportNoteList[]>([]);
    const [isSignedReportByBi, setIsSignedReportByBi] = useState<boolean>(false);

    const [documentFilePath, setDocumentFilePath] = useState<string>("");
    const [showNoteDocViewModal, setShowNoteDocViewModal] = useState<boolean>(false);

    const [showSignatureModal, setShowSignatureModal] = useState<boolean>(false);
    const [signature, setSignature] = useState<string>('');
    const [signatureError, setSignatureError] = useState<string>('');

    /* For send email */
    const [isSendMail, setIsSendMail] = useState<boolean>(false);
    const [showMailModal, setShowMailModal] = useState<boolean>(false);
    const [emailAddresses, setEmailAddresses] = useState<EmailInput[]>([
        { value: '', error: '' },
    ]);

    /* PERMISION HANDLER */
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    /* Check role */
    const [isInspector, setIsInspector] = useState<boolean>(false);

    useEffect(() => {
        const checkRole = async () => {
            const [inspectorResult] = await Promise.all([
                checkIsBuildingInspector(),
            ]);

            setIsInspector(inspectorResult);
        };

        checkRole();
    }, []);
    /*  */

    const havePermissionDownloadSiteVisitReport = useCanEdit("Site Visit Report", "download_site_visit_report");
    const havePermissionShareSiteVisitReport = useCanEdit("Site Visit Report", "share_site_visit_report");
    const havePermissionAddSignature = useCanEdit("Site Visit Report", "add_signature");
    /*  */

    const checkAccessPermission = useCallback(
        (permission: boolean): boolean => {
            return isShared ? canEdit : permission;
        },
        [isShared, canEdit]
    );

    // Fetch data initially from state
    useEffect(() => {
        siteVisitReportDataGet();
    }, [folderId]);

    const siteVisitReportDataGet = async () => {
        await dispatch(fetchSiteVisitReports(folderId));
    }

    useEffect(() => {
        if (location.state && location.state.siteVisitReportData) {
            const hasData = siteVisitReports && siteVisitReports.length > 0;
            const stateData = location.state.siteVisitReportData as SiteVisitReportType;

            if (hasData) {
                const data = siteVisitReports.filter((siteVisitreport) => siteVisitreport.id === stateData.id);
                if (data && data.length > 0) {
                    setSiteVisitReportData(data[0]);
                }
            }
        }
    }, [location.state, siteVisitReports]);

    useEffect(() => {
        if (siteVisitReportData && siteVisitReportData !== null) {
            if (siteVisitReportData?.sign_report === 1) {
                setIsSignedReportByBi(true);
            } else {
                setIsSignedReportByBi(false);
            }
        } else {
            setIsSignedReportByBi(false);
        }
    }, [siteVisitReportData]);


    useEffect(() => {
        if (!siteVisitReportData || siteVisitReportData?.note_list?.length === 0) {
            setSiteVisitReportNotesData([]);
            return;
        }

        setSiteVisitReportNotesData(siteVisitReportData?.note_list ?? []);
    }, [siteVisitReportData]);


    /* *********************************** Mail ********************************** */
    const handleCloseMailModal = () => {
        setShowMailModal(false);
        setEmailAddresses([{ value: '', error: '' }]);
    }

    const handleSendEmail = () => {
        setShowMailModal(true);
    }

    const validateEmail = (email: string): string => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) return 'Email is required.';
        if (!emailRegex.test(email)) return 'Enter a valid email.';
        return '';
    };

    // validations.
    function checkCredentialsEmail(): boolean {
        const updatedEmails = emailAddresses.map((email) => {
            const error = validateEmail(email.value);
            return { ...email, error };
        });

        setEmailAddresses(updatedEmails);

        const hasErrors = updatedEmails.some(email => email.error !== '');
        return !hasErrors;
    }

    function showAPIErrorEmail(keyName: string, error: string): void {
        if (keyName === "email_list[]") {
            const updatedEmails = emailAddresses.map((email) => ({
                ...email,
                error,
            }));
            setEmailAddresses(updatedEmails);
        }
    }

    const sendEmail = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsEmail() || !siteVisitReportData) return;

        setLoading(true);

        try {
            const site_visit_id: number = siteVisitReportData?.id!;
            const email_list: string[] = emailAddresses.map((email) => email.value);

            const data = await sendSiteVisitReportViaEmail(
                site_visit_id,
                email_list
            );

            if (data.statusCode === 200) {
                siteVisitReportDataGet();
                handleCloseMailModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Email sent successfully.",
                    life: 3000,
                });
            } else if (data.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorEmail(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error sending the email.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error sending the email:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while sending the email.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const handleAddMail = () => {
        const lastEmail = emailAddresses[emailAddresses.length - 1];
        const validationMessage = validateEmail(lastEmail.value);

        if (validationMessage) {
            const updatedEmails = [...emailAddresses];
            updatedEmails[updatedEmails.length - 1].error = validationMessage;
            setEmailAddresses(updatedEmails);
            return;
        }

        setEmailAddresses([...emailAddresses, { value: '', error: '' }]);
    };

    const handleRemoveMail = (index: number) => {
        if (emailAddresses.length === 1) return;
        const updatedEmails = emailAddresses.filter((_, i) => i !== index);
        setEmailAddresses(updatedEmails);
    };

    const handleEmailChange = (index: number, newValue: string) => {
        const updatedEmails = [...emailAddresses];
        updatedEmails[index].value = newValue;
        updatedEmails[index].error = '';
        setEmailAddresses(updatedEmails);
    };

    const mailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Send report via mail
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseMailModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const mailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseMailModal}
            />

            <Button
                loading={loading}
                label={`${loading ? 'Sending' : 'Send'}`}
                className="custom_btn primary"
                onClick={sendEmail}
            />
        </div>
    )

    const handleViewReportDocument = () => {
        if (siteVisitReportData?.file_download_path) {
            setShowNoteDocViewModal(true);
            setDocumentFilePath(siteVisitReportData?.file_download_path);
        }
    };
    /* *************************************************************************** */


    /* ********************************** Signature ********************************** */
    const handleCloseSignatureModal = () => {
        setShowSignatureModal(false);
        setSignature('');

        if (isSendMail === true) {
            setIsSendMail(false);
            setEmailAddresses([{ value: '', error: '' }]);
        }
    }

    const handleAddSignature = () => {
        const hasNotes = (siteVisitReportNotesData?.length ?? 0) > 0;

        if (!hasNotes) {
            confirmDialog({
                message:
                    "It looks like you didn't create any notes in this site visit report. Please create at least one note to sign the report.",
                header: 'Information',
                icon: 'bi bi-info-circle',
                defaultFocus: 'accept',
                acceptClassName: 'p-button-success',
                accept: () => navigate(-1),
                acceptLabel: 'Ok',
                rejectLabel: 'Close',
                resizable: false,
                draggable: false,
            });
            return;
        }

        setShowSignatureModal(true);
        setSignature('');
    };

    function checkSignReportCredentials(): boolean {
        const isSignatureValid = checkText(signature, true, setSignatureError);
        const areEmailsValid = isSendMail ? checkCredentialsEmail() : true;

        return isSignatureValid && areEmailsValid;
    }

    function showAPIErrorSignReport(keyName: string, error: string): void {
        if (keyName === "sign") {
            setSignatureError(error);
        }

        if (keyName === "email_list[]" && isSendMail) {
            const updatedEmails = emailAddresses.map((email) => ({
                ...email,
                error,
            }));
            setEmailAddresses(updatedEmails);
        }
    }

    useEffect(() => {
        checkText(signature, false, setSignatureError)
    }, [signature]);

    const handleChangeSendMail = (e: CheckboxChangeEvent) => {
        const send = e.checked;

        if (send === true) {
            setIsSendMail(true);
        } else {
            setIsSendMail(false);
            setEmailAddresses([{ value: '', error: '' }]);
        }
    }

    const handleSignReport = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkSignReportCredentials() || !siteVisitReportData) return;

        setLoading(true);

        try {
            const site_visit_id: number = siteVisitReportData?.id!;
            const sign: string = signature;
            const email_list: string[] = isSendMail ? emailAddresses?.map((email) => email.value) : [];

            const data = await signSiteVisitReportBi(
                site_visit_id,
                sign,
                email_list
            );

            if (data.statusCode === 200) {
                siteVisitReportDataGet();
                handleCloseSignatureModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `The site visit report has been signed ${isSendMail ? 'and shared via email' : ''} successfully.`,
                    life: 3000,
                });
            } else if (data.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorSignReport(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'An error occurred while signing the site visit report.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error signing the site visit report:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while signing the site visit report.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const signatureModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Add signature
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseSignatureModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const signatureModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseSignatureModal}
            />

            <Button
                loading={loading}
                label={`${loading ? 'Processing' : 'Submit'}`}
                className="custom_btn primary"
                onClick={handleSignReport}
            />
        </div>
    )
    /* ******************************************************************************* */


    const handleCloseFileViewModal = () => {
        setShowNoteDocViewModal(false);
    };

    const handleViewNoteDocument = (filePath: string) => {
        if (filePath && filePath !== "") {
            setShowNoteDocViewModal(true);
            setDocumentFilePath(filePath);
        } else {
            setDocumentFilePath("");
        }
    };


    /* ******************************** Download ********************************* */
    const handleDownloadReport = async () => {
        if (isDownloading) return;
        // const reportPath = siteVisitReportData?.file_download_path;
        const reportPath = siteVisitReportData?.file_download_path;
        if (!reportPath) {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "File not found!",
                    life: 3000,
                });
            }
            return;
        }

        setIsDownloading(true);

        setTimeout(async () => {
            try {
                const response = await fetch(reportPath);

                if (!response.ok) {
                    throw new Error("Failed to fetch the file.");
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = reportPath.split("/").pop() || "Site visit report.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "File downloaded successfully.",
                        life: 3000,
                    });
                }
            } catch (error) {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "Error downloading the file",
                        life: 3000,
                    });
                }
            } finally {
                setIsDownloading(false);
            }
        }, 500);
    };
    /* *************************************************************************** */

    const exitReportPreview = () => {
        navigate(
            '/project/detail/default-service/onsite/site-visit-report',
            { state: { data: projectData } }
        );

        goToTop();
    }

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content full_screen_view">
                {siteVisitReportLoading ? (
                    <div className="loading_container">
                        <ProgressSpinner className="progresscircle custom" style={{ width: '50px', height: '50px', stroke: '#8953D7' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration="1s" />
                    </div>
                ) : siteVisitReportError ? (
                    <ErrorPage errorMessage={siteVisitReportError} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-3)}>
                                    {projectData?.job_number}: {projectData?.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData?.folder}
                                </li>
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                                    {projectData?.feature_name}
                                </li>

                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData?.feature_name} detail
                                </li>

                                <li className="page_breadcrumb_item active">Site visit report {siteVisitReportData?.site_visit_no} - Preview</li>
                            </ul>

                            {isInspector && isSignedReportByBi ? (
                                <button className="back_btn p-ripple" onClick={exitReportPreview}>
                                    <i className="bi bi-x-lg me-2"></i>
                                    Close
                                    <Ripple />
                                </button>
                            ) : (
                                <GoBackButton />
                            )}
                        </div>

                        <div className="page_header_area justify-content-end mt-sm-3 mt-2">
                            <div className="page_action_area">
                                {siteVisitReportData?.file_download_path !== null && (
                                    <React.Fragment>
                                        {checkAccessPermission(havePermissionDownloadSiteVisitReport) && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleDownloadReport}
                                                disabled={isDownloading}
                                            >
                                                {isDownloading ? (
                                                    <ProgressSpinner
                                                        style={{ width: "20px", height: "20px" }}
                                                        strokeWidth="5"
                                                        fill="none"
                                                        animationDuration="1s"
                                                        className="spin_loader"
                                                    />
                                                ) : (
                                                    <i className="bi bi-download"></i>
                                                )}
                                                <span>{isDownloading ? "Downloading" : "Download"}</span>
                                                <Ripple />
                                            </button>
                                        )}

                                        {checkAccessPermission(havePermissionShareSiteVisitReport) && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleSendEmail}
                                            >
                                                <i className="bi bi-envelope"></i>
                                                <span>Send</span>
                                                <Ripple />
                                            </button>
                                        )}
                                    </React.Fragment>
                                )}

                                {isInspector && isSignedReportByBi === false && (
                                    <React.Fragment>
                                        {checkAccessPermission(havePermissionAddSignature) && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleAddSignature}
                                            >
                                                <i className="bi bi-pen"></i>
                                                <span>Sign Report</span>
                                                <Ripple />
                                            </button>
                                        )}
                                    </React.Fragment>
                                )}
                            </div>
                        </div>

                        {siteVisitReportData && siteVisitReportData !== null ? (
                            <article className="site_visit_report_container">
                                <div className="sv_report_body">
                                    <div className="site_visit_report_detail_area">
                                        <div className="row">
                                            <div className="col-12 col-sm-6">
                                                <div className="site_visit_report_detail_sub">
                                                    <div className="table-responsive">
                                                        <table className="table table-borderless">
                                                            <thead>
                                                                <tr>
                                                                    <th colSpan={2}>Project Overview</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td className="text_bold">Site Address:</td>
                                                                    <td className="text-capitalize">
                                                                        {siteVisitReportData?.project_data ? (
                                                                            <span>
                                                                                {siteVisitReportData?.project_data?.address ?? '----'}&nbsp;
                                                                            </span>
                                                                        ) : (
                                                                            <span>----</span>
                                                                        )}
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Description:</td>
                                                                    <td className="first_letter_capitalized">
                                                                        {siteVisitReportData?.project_data ? (
                                                                            <span>
                                                                                {siteVisitReportData?.project_data?.description ?? '----'}&nbsp;
                                                                            </span>
                                                                        ) : (
                                                                            <span>----</span>
                                                                        )}
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Building inspector:</td>
                                                                    <td className="text-capitalize">
                                                                        {siteVisitReportData?.building_inspector_data ? (
                                                                            <span>
                                                                                {siteVisitReportData?.building_inspector_data?.name ?? '----'}
                                                                                {siteVisitReportData?.building_inspector_data?.company_name ? (' - ' + siteVisitReportData?.building_inspector_data?.company_name) : ''}
                                                                            </span>
                                                                        ) : (
                                                                            <span>----</span>
                                                                        )}
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Architecture:</td>
                                                                    <td className="text-capitalize">
                                                                        {siteVisitReportData?.architecture_data ? (
                                                                            <span>
                                                                                {siteVisitReportData?.architecture_data?.name ?? '----'}
                                                                                {siteVisitReportData?.architecture_data?.company_name ? (' - ' + siteVisitReportData?.architecture_data?.company_name) : ''}
                                                                            </span>
                                                                        ) : (
                                                                            <span>----</span>
                                                                        )}
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Client Name:</td>
                                                                    <td className="text-capitalize">
                                                                        {siteVisitReportData?.client_name ?? '----'}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-sm-6">
                                                <div className="site_visit_report_detail_sub">
                                                    <div className="table-responsive">
                                                        <table className="table table-borderless">
                                                            <thead>
                                                                <tr>
                                                                    <th colSpan={2}>Site Visit Report Details</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td className="text_bold">Site visit report no.:</td>
                                                                    <td>Site visit report {siteVisitReportData?.site_visit_no}</td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Date:</td>
                                                                    <td>{siteVisitReportData?.date ?? '----'}</td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Visit Conducted:</td>
                                                                    <td className="text-capitalize">
                                                                        {siteVisitReportData?.building_inspector_data ? (
                                                                            <span>
                                                                                {siteVisitReportData?.building_inspector_data?.name ?? '----'}
                                                                            </span>
                                                                        ) : (
                                                                            <span>----</span>
                                                                        )}
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Client Name:</td>
                                                                    <td className="text-capitalize">
                                                                        {siteVisitReportData?.client_name ?? '----'}
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Weather:</td>
                                                                    <td className="first_letter_capitalized">{siteVisitReportData?.weather?.weather_name ?? '----'}</td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="text_bold">Description:</td>
                                                                    <td className="first_letter_capitalized">{siteVisitReportData?.note ?? '----'}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="status_lable_container">
                                        <div className="status_lable_area p-0 my-3">
                                            {noteStatuses?.map((status, index) => (
                                                <div key={`noteStatus${index}`} className="status_lable">
                                                    <span style={{ backgroundColor: status?.colorCode }}></span>
                                                    <h6>{status?.value}</h6>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {siteVisitReportNotesData && siteVisitReportNotesData?.length > 0 ? (
                                        <div className="sv_report_table_area table-responsive pl_table_area">
                                            <table className="table table-bordered sv_report_table pl_table mb-0">
                                                <thead>
                                                    <tr className="sv_report_table_header light">
                                                        <th style={{ width: "5%" }} className="item_code_column">
                                                            Note no.
                                                        </th>

                                                        <th style={{ width: "80%" }} className="comment_column">
                                                            Note
                                                        </th>

                                                        <th style={{ width: "15%" }} className="files_column">
                                                            Files
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {siteVisitReportNotesData?.map((note) => {
                                                        const biResponse: SiteVisitReportBuildingInspectorResponse | null = note?.response_building_inspector || null;
                                                        const biResponseFiles: ResponseFileList[] = note?.response_building_inspector?.file_list || [];
                                                        let colorCode = noteStatuses?.find(status => status?.value === note?.status)?.colorCode || '';

                                                        return (
                                                            <tr
                                                                key={`${siteVisitReportData?.inspection_for}_item_${note?.id}`}
                                                                className="sv_report_table_data inner_tabel_row"
                                                                style={{ backgroundColor: colorCode }}
                                                            >
                                                                <td style={{ width: "5%" }} className="item_code_column">
                                                                    {note?.note_no}.
                                                                </td>

                                                                <td style={{ width: "80%" }} className="comment_column first_letter_capitalized">
                                                                    {note?.note}
                                                                </td>

                                                                <td style={{ width: "15%" }} className="files_column">
                                                                    <div className="item_file_area mt-1">
                                                                        {biResponse && biResponseFiles && biResponseFiles?.length > 0 ? (
                                                                            <React.Fragment>
                                                                                {biResponseFiles?.map((file) => {
                                                                                    return (
                                                                                        <React.Fragment>
                                                                                            {file?.extension === "pdf" ? (
                                                                                                <div className="item_file_label">
                                                                                                    <div
                                                                                                        className="item_doc p-ripple"
                                                                                                        onClick={() => handleViewNoteDocument(file?.path!)}
                                                                                                    >
                                                                                                        <i className="bi bi-file-earmark-pdf"></i>
                                                                                                    </div>
                                                                                                </div>
                                                                                            ) : (
                                                                                                <div className="item_file_label">
                                                                                                    <ImagePreview
                                                                                                        src={`${file?.path}`}
                                                                                                        alt={`sv_report_image_${file?.id}`}
                                                                                                    />
                                                                                                </div>
                                                                                            )}
                                                                                        </React.Fragment>
                                                                                    );
                                                                                }
                                                                                )}
                                                                            </React.Fragment>
                                                                        ) : (
                                                                            <div className="no_file_found">
                                                                                ----
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="no_report_data">
                                            <h6>
                                                <i className="bi bi-exclamation-circle me-1"></i>
                                                No note(s) were included in this site visit report!
                                            </h6>
                                        </div>
                                    )}

                                    {isSignedReportByBi && (
                                        <div className="sv_report_footer">
                                            <div className="sv_report_footer_item">
                                                <h3>
                                                    {siteVisitReportData?.sign}
                                                </h3>

                                                <h5>Building Inspector</h5>
                                                <h6>{siteVisitReportData?.sign_date}</h6>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </article>
                        ) : (
                            <div className="page_main_content_section no_header">
                                <NoDataCreated
                                    message={`No data found!`}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>


            {/* Signature modal */}
            <Dialog
                header={signatureModalHeader}
                footer={signatureModalFooter}
                headerClassName="custom_modal_header"
                visible={showSignatureModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseSignatureModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                <div className="page_form_group mb-3">
                                    <label htmlFor="signature" className="custom_form_label is_required">Signature</label>

                                    <InputText
                                        id='signature'
                                        value={signature}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignature(e.target.value)}
                                        className="custom_form_input signature_input"
                                        placeholder='Enter your signature'
                                        autoFocus
                                        type='text'
                                    />

                                    {signatureError && (<small className="form_error_msg">{signatureError}</small>)}
                                </div>
                            </div>

                            {siteVisitReportData?.shared_email_list && siteVisitReportData?.shared_email_list?.length > 0 && (
                                <div className="col-12">
                                    <div className="report_mail_note">
                                        <h6>
                                            Note:
                                            <div>
                                                Sign the report and send it to the following email addresses.

                                                <p>
                                                    {siteVisitReportData?.shared_email_list.join(", ")}
                                                </p>
                                            </div>
                                        </h6>
                                    </div>
                                </div>
                            )}

                            <div className="col-12">
                                <div className="page_form_group">
                                    <div className="response_state">
                                        <Checkbox
                                            inputId="isSendMail"
                                            name="isSendMail"
                                            onChange={(e: CheckboxChangeEvent) => handleChangeSendMail(e)}
                                            checked={isSendMail}
                                        />
                                        <label htmlFor="isSendMail">Additionally, send the report via more emails?</label>
                                    </div>
                                </div>
                            </div>

                            {isSendMail && (
                                <div className="col-12">
                                    {emailAddresses.map((email, index) => (
                                        <div className="mail_input_area d-flex align-items-center mb-2" key={index}>
                                            <TextInput
                                                id={`emailAddress-${index}`}
                                                label={`E-mail address ${index + 1}`}
                                                labelHtmlFor={`emailAddress-${index}`}
                                                required={true}
                                                value={email.value}
                                                placeholder="abcd@gmail.com"
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    handleEmailChange(index, e.target.value)
                                                }
                                                error={email.error}
                                                keyFilter={'email'}
                                                formGroupClassName="mb-1"
                                            />
                                            {emailAddresses.length > 1 && (
                                                <i
                                                    className="bi bi-dash-circle mail_remove_btn"
                                                    title="Remove"
                                                    onClick={() => handleRemoveMail(index)}
                                                ></i>
                                            )}
                                        </div>
                                    ))}

                                    <button className={`add_data_btn p-ripple`} onClick={handleAddMail}>
                                        <i className={`bi bi-plus-circle`}></i>
                                        Add mail
                                        < Ripple />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Mail modal */}
            <Dialog
                header={mailModalHeader}
                footer={mailModalFooter}
                headerClassName="custom_modal_header"
                visible={showMailModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseMailModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                {emailAddresses.map((email, index) => (
                                    <div className="mail_input_area d-flex align-items-center mb-2" key={index}>
                                        <TextInput
                                            id={`emailAddress-${index}`}
                                            label={`E-mail address ${index + 1}`}
                                            labelHtmlFor={`emailAddress-${index}`}
                                            required={true}
                                            value={email.value}
                                            placeholder="abcd@gmail.com"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleEmailChange(index, e.target.value)
                                            }
                                            error={email.error}
                                            keyFilter={'email'}
                                            formGroupClassName="mb-1"
                                        />
                                        {emailAddresses.length > 1 && (
                                            <i
                                                className="bi bi-dash-circle mail_remove_btn"
                                                title="Remove"
                                                onClick={() => handleRemoveMail(index)}
                                            ></i>
                                        )}
                                    </div>
                                ))}

                                <button className={`add_data_btn p-ripple`} onClick={handleAddMail}>
                                    <i className={`bi bi-plus-circle`}></i>
                                    Add mail
                                    < Ripple />
                                </button>
                            </div>

                            <div className="col-12 mt-3">
                                {siteVisitReportData?.file_download_path !== null && (
                                    <div
                                        className="file_attached_area"
                                        onClick={handleViewReportDocument}
                                    >
                                        <i className="bi bi-paperclip"></i>
                                        <h6>Site vist report.pdf</h6>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showNoteDocViewModal}
                onClose={handleCloseFileViewModal}
                fileUrl={documentFilePath}
            />
            {/*  */}
        </React.Fragment >
    )
}

export default SiteVisitReportPreview;